// Gating de la commande DISARM au cockpit.
// landed_state (MAV_LANDED_STATE) : 1=ON_GROUND, 2=IN_AIR, null/undefined=inconnu.
// Règle : armed=true ne signifie JAMAIS in_air. On s'appuie sur landed_state réel.
export const LANDED_ON_GROUND = 1;
export const LANDED_IN_AIR = 2;

export function disarmGate({ linkReady, busy, armed, landedState }) {
  if (!linkReady) return { disabled: true, reason: "Liaison requise" };
  if (busy) return { disabled: true, reason: "" };
  if (!armed) return { disabled: true, reason: "Déjà désarmé" };
  if (landedState === LANDED_IN_AIR) return { disabled: true, reason: "Interdit en vol" };
  if (landedState === LANDED_ON_GROUND) return { disabled: false, reason: "" };
  // landed_state inconnu : conservateur (on bloque) MAIS sans prétendre à tort "interdit en vol".
  return { disabled: true, reason: "État sol inconnu" };
}

// Compte à rebours d'auto-désarmement PX4 (COM_DISARM_PRFLT). Calé sur executed_at réel :
// remaining = max(0, prflt - (now - executed_at)). Si ARM exécuté il y a 3 s -> commence à ~7 s.
export function armAutoDisarmRemaining(executedAt, nowMs = Date.now(), prflt = 10) {
  const t = executedAt ? Date.parse(executedAt) : nowMs;
  const base = Number.isFinite(t) ? t : nowMs;
  const elapsed = (nowMs - base) / 1000;
  return Math.max(0, Math.ceil(prflt - elapsed));
}

// Fraîcheur de la télémétrie (gating strict) : True si la dernière donnée est trop ancienne
// ou absente. Une liaison MAVLink "true" mais périmée ne doit PAS autoriser une commande.
export function isTelemetryStale(lastSeen, nowMs = Date.now(), thresholdS = 6) {
  if (!lastSeen) return true;
  const t = Date.parse(lastSeen);
  if (!Number.isFinite(t)) return true;
  return (nowMs - t) / 1000 > thresholdS;
}

// --------------------------------------------------------------------------- //
// MATRIX.1 — Séquence de vol imposée : PREPARE -> ARM -> TAKEOFF.
// Le CommandCenter ne doit JAMAIS permettre un flux physique direct ARM -> TAKEOFF.
// Ces fonctions sont PURES (testables sans React) et ne dupliquent PAS la logique de
// sécurité de l'Agent : elles gèrent uniquement le gating UI du séquencement.
// `prepared` = un prepare_takeoff a atteint le statut terminal 'executed' (jamais result_unknown).
// --------------------------------------------------------------------------- //

// MATRIX.1.1 — tant que la commande active est result_unknown (incertitude réconciliable),
// PREPARE / ARM / TAKEOFF sont verrouillés : on attend un statut autoritaire. LAND/RTL de
// récupération ne sont PAS concernés (gates télémétriques propres).
export const UNCERTAIN_REASON = "Résultat précédent incertain — attendre la réconciliation";

// PREPARE : première étape (drone désarmé, au sol). Précède obligatoirement l'ARM.
export function prepareGate({ linkReady, busy, armed, gps, batOk, prepared, uncertain, identityBlock }) {
  if (uncertain) return { disabled: true, reason: UNCERTAIN_REASON };
  if (identityBlock) return { disabled: true, reason: identityBlock };
  if (!linkReady) return { disabled: true, reason: "Liaison requise" };
  if (busy) return { disabled: true, reason: "" };
  if (armed) return { disabled: true, reason: "Déjà armé" };
  if (prepared) return { disabled: true, reason: "Déjà préparé" };
  if (!gps) return { disabled: true, reason: "GPS/position requis" };
  if (!batOk) return { disabled: true, reason: "Batterie insuffisante (<15%)" };
  return { disabled: false, reason: "" };
}

// ARM : autorisé UNIQUEMENT après un PREPARE 'executed'. Jamais de contournement.
export function armGate({ linkReady, busy, armed, prepared, uncertain, identityBlock }) {
  if (uncertain) return { disabled: true, reason: UNCERTAIN_REASON };
  if (identityBlock) return { disabled: true, reason: identityBlock };
  if (!linkReady) return { disabled: true, reason: "Liaison requise" };
  if (busy) return { disabled: true, reason: "" };
  if (armed) return { disabled: true, reason: "Déjà armé" };
  if (!prepared) return { disabled: true, reason: "Préparer d'abord (PREPARE)" };
  return { disabled: false, reason: "" };
}

// TAKEOFF : autorisé UNIQUEMENT après PREPARE 'executed' ET ARM confirmé (télémétrie armed=true).
export function takeoffGate({ linkReady, busy, armed, gps, batOk, prepared, uncertain, identityBlock }) {
  if (uncertain) return { disabled: true, reason: UNCERTAIN_REASON };
  if (identityBlock) return { disabled: true, reason: identityBlock };
  if (!linkReady) return { disabled: true, reason: "Liaison requise" };
  if (busy) return { disabled: true, reason: "" };
  if (!prepared) return { disabled: true, reason: "Préparer d'abord (PREPARE puis ARM)" };
  if (!armed) return { disabled: true, reason: "Armer d'abord (fenêtre 10 s)" };
  if (!gps) return { disabled: true, reason: "GPS/position requis" };
  if (!batOk) return { disabled: true, reason: "Batterie insuffisante (<15%)" };
  return { disabled: false, reason: "" };
}

// --------------------------------------------------------------------------- //
// MATRIX.5 — Gate d'IDENTITÉ contrôleur + capacités (garde UX ; l'Agent reste l'autorité).
// Un NOUVEAU démarrage de vol (PREPARE/ARM/TAKEOFF/OFFBOARD) est bloqué si identity_ready !== true.
// JAMAIS appliqué à LAND/RTL/DISARM (récupération préservée). Si le descriptor est ABSENT (ancien
// Agent / donnée non encore propagée), on NE gate PAS côté UI (l'Agent fail-closed reste l'autorité).
// --------------------------------------------------------------------------- //
export const OFFBOARD_REQUIRED_CAPS = [
  "EXTERNAL_CONTROL", "BODY_VELOCITY", "LOCAL_POSITION_HOLD", "MODE_TRANSITION",
];

export function identityNewFlightBlocked(fc) {
  if (!fc) return null;                       // descriptor absent -> pas de gating UI
  if (fc.identity_ready === true) return null;
  const s = fc.identity_state;
  if (s === "MISMATCH") return "Contrôleur détecté ≠ configuré (MISMATCH) — démarrage bloqué";
  if (s === "DISCONNECTED") return "Lien MAVLink absent — démarrage indisponible";
  return "Identité contrôleur non confirmée (UNKNOWN) — démarrage bloqué";
}

export function offboardCapabilityBlocked(fc) {
  if (!fc) return null;
  const idb = identityNewFlightBlocked(fc);
  if (idb) return idb;
  const caps = fc.capabilities || [];
  const missing = OFFBOARD_REQUIRED_CAPS.filter((c) => !caps.includes(c));
  if (missing.length) return "Capacité(s) OFFBOARD manquante(s): " + missing.join(", ");
  return null;
}

// result_unknown : résultat INDÉTERMINÉ / réconciliable. N'est NI un succès, NI un échec, NI un
// PX4_TIMEOUT. Une étape dépendante ne peut avancer QUE sur 'executed' (jamais sur result_unknown).
export const RESULT_UNKNOWN = "result_unknown";
export function isResultUnknown(status) {
  return status === RESULT_UNKNOWN;
}
export function stepUnlocked(status) {
  return status === "executed";
}
